import { useEffect, useRef } from 'react';

interface Props {
  peaks?: number[] | null; startSec?: number; durationSec?: number; volume?: number; color?: string; className?: string;
}

export function WaveformCanvas({ peaks, startSec = 0, durationSec, volume = 1.0, color = 'rgba(255, 255, 255, 0.55)', className }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    function render() {
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect(), w = Math.floor(rect.width), h = Math.floor(rect.height);
      if (w <= 0 || h <= 0) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = Math.floor(w * dpr); canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0); ctx.clearRect(0, 0, w, h);

      const isSilent = !peaks || !peaks.length || peaks.every(p => p < 0.001);
      const centerY = h / 2;
      if (isSilent) {
        ctx.beginPath(); ctx.setLineDash([4, 4]); ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)'; ctx.lineWidth = 1;
        ctx.moveTo(6, centerY); ctx.lineTo(w - 6, centerY); ctx.stroke(); ctx.setLineDash([]);
        return;
      }

      const pts = 20, sIdx = Math.max(0, Math.floor(startSec * pts)), eIdx = durationSec !== undefined ? Math.min(peaks.length, Math.ceil((startSec + durationSec) * pts)) : peaks.length;
      const segPeaks = peaks.slice(sIdx, eIdx);
      if (!segPeaks.length || segPeaks.every(p => p < 0.001)) {
        ctx.beginPath(); ctx.setLineDash([4, 4]); ctx.strokeStyle = 'rgba(255, 255, 255, 0.25)'; ctx.lineWidth = 1;
        ctx.moveTo(4, centerY); ctx.lineTo(w - 4, centerY); ctx.stroke(); ctx.setLineDash([]);
        return;
      }

      const barW = 2, gap = 1, numBars = Math.max(1, Math.floor(w / (barW + gap))), step = segPeaks.length / numBars;
      ctx.fillStyle = color;
      for (let i = 0; i < numBars; i++) {
        let maxVal = 0;
        const start = Math.floor(i * step), end = Math.min(segPeaks.length, Math.ceil((i + 1) * step));
        for (let j = start; j < end; j++) if (segPeaks[j] > maxVal) maxVal = segPeaks[j];
        const barH = Math.max(2, Math.min(1.0, maxVal * Math.max(0, volume)) * (h - 4));
        ctx.fillRect(i * (barW + gap), centerY - barH / 2, barW, barH);
      }
    }

    render();
    const ro = new ResizeObserver(render);
    ro.observe(canvas);
    return () => ro.disconnect();
  }, [peaks, startSec, durationSec, volume, color]);

  return <canvas ref={canvasRef} className={className} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }} />;
}
